import React from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiCheck, FiUser, FiBookOpen, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import useActiveAttendanceSessions from './useActiveAttendanceSessions';
import styles from './ActiveAttendanceSessions.module.css';

function ActiveAttendanceSessions() {
  const {
    sessions, loading, error, success, checkingIn, updateAvailable, getVisibleSessions,
    handleCheckIn, getSessionStatus, getCheckedInTimeLabel, extractWeekNumber, getTimeRangeLabel,
    isCheckInWindowActive, isLiveSessionActive, getVirtualTimeRangeLabel
  } = useActiveAttendanceSessions();

  if (loading && sessions.length === 0) {
    return (
      <div className={styles.attendanceCard}>
        <div className={styles.attendanceHeader}>
          <h2 className={styles.attendanceTitle}>
            <FiClock size={20} />
            Active Attendance Sessions
          </h2>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <span>Loading active sessions...</span>
        </div>
      </div>
    );
  }

  // Don't show the component at all if there are no sessions or no visible sessions
  if (sessions.length === 0 || getVisibleSessions().length === 0) {
    return null;
  }

  return (
    <div className={styles.attendanceCard}>
      <div className={styles.attendanceHeader}>
        <h2 className={styles.attendanceTitle}>
          <FiClock size={20} />
          My Active Sessions
        </h2>
      </div>

      <div className={styles.attendanceList}>
        {/* Update available banner */}
        {updateAvailable && (
          <motion.div
            className={styles.updateBanner}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className={styles.updateBannerContent}>
              <FiRefreshCw size={16} />
              <span>A new version is available. Please refresh to continue.</span>
            </div>
            <button
              className={styles.updateRefreshButton}
              onClick={() => window.location.reload(true)}
            >
              Refresh Now
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            className={styles.successMessage}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FiCheck size={16} />
            {success}
          </motion.div>
        )}

        {error && !updateAvailable && (
          <motion.div
            className={styles.errorMessage}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FiAlertCircle size={16} />
            {error}
          </motion.div>
        )}

        {getVisibleSessions().map((session, index) => {
          const sessionStatus = getSessionStatus(session);
          const isCheckedIn = sessionStatus === 'checked-in';
          const isEnded = sessionStatus === 'ended';

          return (
            <motion.div
              key={session.session_id}
              className={styles.sessionGrid}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0 }}
            >
              {/* Combined Session Card */}
              <div className={`${styles.sessionCard} ${styles.sessionCard__checkin} ${isCheckedIn ? styles['sessionCard__checkin--completed'] : ''}`}>
                {/* Card Header */}
                <div className={`${styles.cardHeader} ${styles.cardHeader__checkin} ${isCheckedIn ? styles['cardHeader__checkin--completed'] : ''}`}>
                  Session{extractWeekNumber(session.title) ? ` · Week ${extractWeekNumber(session.title)}` : ''}
                </div>

                {/* Course Info */}
                <div className={styles.sessionContent}>
                  <div className={styles.courseInfo}>
                    <div className={styles.courseNameRow}>
                      <FiBookOpen size={16} className={styles.courseIcon} />
                      <span className={styles.courseName}>
                        {session.course_code || session.elective_code} · {session.course_name || session.elective_name}
                      </span>
                    </div>
                  </div>
                  <div className={styles.instructorInfo}>
                    <FiUser size={14} className={styles.instructorIcon} />
                    <span className={styles.instructorName}>
                      {session.entity_type === 'elective' && session.instructors ? session.instructors : `${session.instructor_first_name} ${session.instructor_last_name}`}
                    </span>
                  </div>
                </div>

                {/* Stacked sections: Virtual Session + Check-In Window */}
                <div className={styles.combinedSections}>
                  {/* Virtual Session row */}
                  {/* Check-In Window row */}
                  <div className={styles.combinedSection}>
                    <div className={styles.combinedSectionLeft}>
                      <div className={styles.sessionTypeWrapper}>
                        <div className={`${styles.sessionTypeLabel} ${styles.label__checkin} ${isCheckedIn ? styles['label__checkin--completed'] : ''}`}>
                          CHECK-IN WINDOW
                        </div>
                        {isCheckInWindowActive(session) && (
                          <div className={`${styles.activeLabel} ${styles.activeBadge__checkin} ${isCheckedIn ? styles['activeBadge__checkin--completed'] : ''}`}>
                            ACTIVE
                          </div>
                        )}
                      </div>
                      <div className={`${styles.timeDisplay} ${styles.time__checkin} ${isCheckedIn ? styles['time__checkin--completed'] : ''}`}>
                        {getTimeRangeLabel(session)}
                      </div>
                    </div>
                    <div className={styles.combinedSectionRight}>
                      {(() => {
                        if (isCheckedIn) {
                          const checkedInTime = getCheckedInTimeLabel(session);
                          return (
                            <button
                              className={`${styles.actionButton} ${styles.actionButton__checkedIn}`}
                              disabled={true}
                            >
                              Checked In{checkedInTime && ` at ${checkedInTime}`}
                            </button>
                          );
                        }
                        if (isEnded) {
                          return (
                            <button
                              className={`${styles.actionButton} ${styles.actionButton__closed}`}
                              disabled={true}
                            >
                              Closed
                            </button>
                          );
                        }
                        return (
                          <button
                            className={`${styles.actionButton} ${styles.actionButton__checkin}`}
                            onClick={() => handleCheckIn(session.session_id)}
                            disabled={checkingIn.has(session.session_id)}
                          >
                            {checkingIn.has(session.session_id) ? (
                              <div className={styles.loadingSpinner} />
                            ) : (
                              'Complete Check-In'
                            )}
                          </button>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Virtual Session row */}
                  {session.virtual_link && (
                    <>
                      <hr className={styles.combinedSectionDivider} />
                      <div className={styles.combinedSection}>
                        <div className={styles.combinedSectionLeft}>
                          <div className={styles.sessionTypeWrapper}>
                            <div className={`${styles.sessionTypeLabel} ${styles.label__live}`}>
                              VIRTUAL SESSION
                            </div>
                            {isLiveSessionActive(session) && (
                              <div className={`${styles.activeLabel} ${styles.activeBadge__live}`}>
                                ACTIVE
                              </div>
                            )}
                          </div>
                          <div className={`${styles.timeDisplay} ${styles.time__live}`}>
                            {getVirtualTimeRangeLabel(session)}
                          </div>
                        </div>
                        <div className={styles.combinedSectionRight}>
                          <a
                            className={`${styles.actionButton} ${styles.actionButton__live} ${styles.actionButton__noHover}`}
                            href={session.virtual_link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Join Teams Meeting
                          </a>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default ActiveAttendanceSessions;
